const claimedRewardIds = new Set();
const RETURN_LOCATION_KEY_PREFIX = 'botgo.featured-preview.return.v1:';
const DISMISSED_REWARD_KEY_PREFIX = 'botgo.featured-reward.dismissed.v1:';
const RETURN_LOCATION_TTL_MS = 60 * 60 * 1000;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

export function getFeaturedRewardPostId(reward) {
  const postId = String(reward?.payload?.postId || reward?.entityId || '').trim();
  return UUID_PATTERN.test(postId) ? postId.toLowerCase() : null;
}

export function formatFeaturedRewardWorkId(reward) {
  const postId = getFeaturedRewardPostId(reward);
  return postId ? `${postId.slice(0, 8)}...` : '—';
}

export function getFeaturedRewardTarget(reward) {
  const postId = getFeaturedRewardPostId(reward);
  if (postId) return `/post/${postId}?from=notifications`;
  const deepLink = typeof reward?.deepLink === 'string' ? reward.deepLink.trim() : '';
  return deepLink.startsWith('/') ? deepLink : '/profile';
}

export function rememberFeaturedRewardReturnLocation(reward) {
  const postId = getFeaturedRewardPostId(reward);
  if (!postId || typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      `${RETURN_LOCATION_KEY_PREFIX}${postId}`,
      JSON.stringify({ href: window.location.href, savedAt: Date.now() }),
    );
  } catch {
    // Storage can be unavailable in private browsing. The route keeps a
    // same-origin referrer and Profile fallback for that case.
  }
}

export function consumeFeaturedRewardReturnLocation(postId) {
  const normalizedPostId = String(postId || '').trim().toLowerCase();
  if (!UUID_PATTERN.test(normalizedPostId) || typeof window === 'undefined') return null;
  const key = `${RETURN_LOCATION_KEY_PREFIX}${normalizedPostId}`;
  try {
    const raw = window.sessionStorage.getItem(key);
    window.sessionStorage.removeItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.savedAt);
    const target = new URL(String(parsed?.href || ''), window.location.origin);
    if (
      target.origin !== window.location.origin
      || !Number.isFinite(savedAt)
      || Date.now() - savedAt > RETURN_LOCATION_TTL_MS
      || target.href === window.location.href
    ) return null;
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return null;
  }
}

export function claimFeaturedRewardNotice(rewardId) {
  const normalizedId = String(rewardId || '').trim();
  if (!normalizedId || claimedRewardIds.has(normalizedId)) return false;

  if (typeof window !== 'undefined') {
    try {
      if (window.localStorage.getItem(`${DISMISSED_REWARD_KEY_PREFIX}${normalizedId}`)) return false;
    } catch {
      // Server-side read state still handles storage-restricted browsers.
    }
  }

  claimedRewardIds.add(normalizedId);
  return true;
}

export function dismissFeaturedRewardNotice(rewardId) {
  const normalizedId = String(rewardId || '').trim();
  if (!normalizedId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${DISMISSED_REWARD_KEY_PREFIX}${normalizedId}`, '1');
  } catch {
    // The notification API remains the persistent fallback.
  }
}
